import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useRef,
  useState,
} from "react";
import axios from "axios";
// Imported from data_requests directly rather than the "../services" barrel:
// data_requests imports useAuth back from this file, and going through the
// barrel would pull web3 + walletService into that cycle.
import { hostURL, postLogout } from "../services/data_requests";

const AuthContext = createContext();

// localStorage keys this context owns. Cleared on logout / de-auth - note that
// unrelated keys (cookieConsent) must survive, so we never call localStorage.clear().
const AUTH_STORAGE_KEYS = [
  "isAuthenticated",
  "userEmail",
  "ethAddress",
  "loginMethod",
  "token",
];

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [ethAddress, setEthAddress] = useState(
    localStorage.getItem("ethAddress") || null,
  );
  // Seeded optimistically so a valid session never flickers through the
  // logged-out UI while the bootstrap request below is in flight.
  const [isAuthenticated, setIsAuthenticated] = useState(
    localStorage.getItem("isAuthenticated") === "true" ||
      !!localStorage.getItem("ethAddress"),
  );
  const [userEmail, setUserEmail] = useState(
    () => localStorage.getItem("userEmail") || null,
  );
  const [authStatus, setAuthStatus] = useState("unknown");

  // Bumped by every auth mutation. In-flight requests capture the value and
  // bail on resolve if it changed, so a slow bootstrap 401 can't clobber a
  // login that completed while it was still pending.
  const authEpochRef = useRef(0);
  const didBootstrapRef = useRef(false);
  const loggingOutRef = useRef(false);
  // Read inside the bootstrap catch, where the isAuthenticated closure is stale.
  const isAuthenticatedRef = useRef(isAuthenticated);
  isAuthenticatedRef.current = isAuthenticated;

  const applyUser = useCallback((data) => {
    setUser(data);
    setIsAuthenticated(true);
    setAuthStatus("authenticated");
    localStorage.setItem("isAuthenticated", "true");

    if (data.email) {
      setUserEmail(data.email);
      localStorage.setItem("userEmail", data.email);
    }
    if (data.eth_account_address) {
      setEthAddress(data.eth_account_address);
      localStorage.setItem("ethAddress", data.eth_account_address);
    }
  }, []);

  const clearAuth = useCallback(() => {
    authEpochRef.current += 1;
    AUTH_STORAGE_KEYS.forEach((key) => localStorage.removeItem(key));

    setUser(null);
    setEthAddress(null);
    setUserEmail(null);
    setIsAuthenticated(false);
    setAuthStatus("unauthenticated");
  }, []);

  const refreshUser = useCallback(async () => {
    const epoch = authEpochRef.current;
    try {
      const { data } = await axios.get(`${hostURL}user`, {
        withCredentials: true,
      });
      if (authEpochRef.current !== epoch) return null;

      if (data && typeof data === "object") {
        applyUser(data);
        return data;
      }
      clearAuth();
      return null;
    } catch (error) {
      if (authEpochRef.current !== epoch) return null;

      const status = error?.response?.status;
      if (status === 401 || status === 403) clearAuth();
      return null;
    }
  }, [applyUser, clearAuth]);

  // Validate the cached localStorage state against the server exactly once on
  // load. Without this an expired session cookie leaves isAuthenticated stuck
  // at true, so guarded pages render and every request 401s "Invalid token".
  useEffect(() => {
    if (didBootstrapRef.current) return;
    didBootstrapRef.current = true;

    const epoch = authEpochRef.current;

    axios
      .get(`${hostURL}user`, { withCredentials: true })
      .then(({ data }) => {
        if (authEpochRef.current !== epoch) return;
        if (data && typeof data === "object") applyUser(data);
        else clearAuth();
      })
      .catch((error) => {
        if (authEpochRef.current !== epoch) return;

        const status = error?.response?.status;
        if (status === 401 || status === 403) {
          clearAuth();
          return;
        }

        // A network failure or 5xx says nothing about the session - keep the
        // cached state rather than logging everyone out during an outage.
        console.warn(
          "Auth bootstrap failed, keeping cached state:",
          error?.message,
        );
        setAuthStatus(
          isAuthenticatedRef.current ? "authenticated" : "unauthenticated",
        );
      });
  }, [applyUser, clearAuth]);

  const loginWithWeb3 = useCallback((address) => {
    authEpochRef.current += 1;
    localStorage.setItem("ethAddress", address);
    localStorage.setItem("isAuthenticated", "true");

    setEthAddress(address);
    setIsAuthenticated(true);
    setAuthStatus("authenticated");
  }, []);

  const loginWithEmail = useCallback((email) => {
    authEpochRef.current += 1;
    localStorage.setItem("userEmail", email);
    localStorage.setItem("isAuthenticated", "true");

    setUserEmail(email);
    setIsAuthenticated(true);
    setAuthStatus("authenticated");
  }, []);

  // Local-only merge for optimistic edits. Never call this on a timer: the four
  // useToGetUserData consumers key effects off the user object's identity.
  const updateUser = useCallback((patch) => {
    setUser((prev) => (prev ? { ...prev, ...patch } : prev));
  }, []);

  const logout = useCallback(async () => {
    if (loggingOutRef.current) return;
    loggingOutRef.current = true;

    // Invalidate the server session too - clearing localStorage alone left the
    // cookie live until it expired on its own.
    try {
      await postLogout();
    } catch (err) {
      console.error("Logout request failed:", err);
    }

    clearAuth();
    // No state-driven navigation here: clearing isAuthenticated re-renders the
    // guarded page and bounces through /auth before this navigation lands.
    // The full page load rebuilds state from the now-empty localStorage.
    window.location.replace("/");
  }, [clearAuth]);

  return (
    <AuthContext.Provider
      value={{
        user,
        ethAddress,
        userEmail,
        isAuthenticated,
        authStatus,
        authLoading: authStatus === "unknown",
        loginWithWeb3,
        loginWithEmail,
        refreshUser,
        updateUser,
        clearAuth,
        logout,
        setEthAddress,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
};
export const useAuth = () => useContext(AuthContext);
